import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  Chip,
  Alert,
  Collapse,
  Paper,
  InputAdornment,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  InfoOutlined as InfoOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentPaste as ContentPasteIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import {
  MCP_CATALOG,
  fetchLatestStdioVersion,
  buildStdioArgs,
  type StdioCatalogKey,
  type StdioCommand,
  type EnvVarRow,
  type EnvVarType,
} from '../services/skillsService';

const STDIO_COMMANDS: StdioCommand[] = ['uvx', 'npx', 'python'];

export interface StdioMcpFieldsValue {
  stdio_catalog: StdioCatalogKey;
  stdio_command: StdioCommand;
  stdio_package: string;
  stdio_version: string;
  stdio_env_rows: EnvVarRow[];
}

export interface StdioMcpFieldsProps {
  value: StdioMcpFieldsValue;
  onChange: (partial: Partial<StdioMcpFieldsValue>) => void;
  onPrefillBusinessRules?: (text: string) => void;
}

// ── Type badge colours ────────────────────────────────────────────────────────

const TYPE_BADGE: Record<EnvVarType, { label: string; color: 'default' | 'primary' | 'error' | 'info' | 'warning' | 'success' | 'secondary' }> = {
  fixed:  { label: 'FIXED',  color: 'default'   },
  vault:  { label: 'VAULT',  color: 'info'       },
  secret: { label: 'SECRET', color: 'error'      },
  plain:  { label: 'PLAIN',  color: 'default'    },
};

// ── JSON preview builder ──────────────────────────────────────────────────────

function buildPreviewConfig(value: StdioMcpFieldsValue): Record<string, unknown> {
  const cfg: Record<string, unknown> = {
    protocol: 'mcp',
    transport: 'stdio',
    stdio_command: value.stdio_command,
    stdio_args: buildStdioArgs(value.stdio_package, value.stdio_version),
  };
  for (const row of value.stdio_env_rows) {
    const k = row.key.trim();
    if (!k) continue;
    // secret rows are ALWAYS masked in the preview — never expose the real value
    // regardless of whether the eye-icon toggle is on or whether the field is populated.
    // vault rows show their reference placeholder; plain/fixed rows show their value.
    cfg[`env_${k}`] = row.type === 'vault'
      ? `{{vault:${row.vault_label ?? k.toLowerCase()}}}`
      : row.type === 'secret'
      ? '••••••'
      : row.value;
  }
  return cfg;
}

// ── Syntax-coloured JSON line ────────────────────────────────────────────────

const JsonLine: React.FC<{ k: string; v: unknown }> = ({ k, v }) => {
  const theme = useTheme();
  const keyColor = theme.palette.error.main;
  const strColor = theme.palette.success.main;
  const vaultColor = theme.palette.info.main;

  let display: string;
  if (Array.isArray(v)) {
    display = JSON.stringify(v);
  } else {
    display = JSON.stringify(v);
  }
  const isVault = typeof v === 'string' && v.startsWith('{{vault:');
  const color = isVault ? vaultColor : strColor;

  return (
    <Box component="span" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.7 }}>
      {'  '}
      <Box component="span" sx={{ color: keyColor }}>{`"${k}"`}</Box>
      {': '}
      <Box component="span" sx={{ color }}>{display}</Box>
      {','}
    </Box>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const StdioMcpFields: React.FC<StdioMcpFieldsProps> = ({ value, onChange, onPrefillBusinessRules }) => {
  const [versionLoading, setVersionLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [secretVisible, setSecretVisible] = useState<Record<number, boolean>>({});

  const isCatalog = value.stdio_catalog !== 'custom';
  const catalogEntry = MCP_CATALOG[value.stdio_catalog];

  // ── Catalog selection ─────────────────────────────────────────────────────

  const applyCatalogEntry = async (key: StdioCatalogKey) => {
    const entry = MCP_CATALOG[key];
    if (key === 'custom') {
      onChange({
        stdio_catalog: 'custom',
        stdio_command: 'uvx',
        stdio_package: '',
        stdio_version: '',
        stdio_env_rows: [],
      });
      return;
    }

    let stdio_env_rows: EnvVarRow[];
    if (entry.envRows) {
      stdio_env_rows = entry.envRows.map((r) => ({
        key: r.key,
        value: r.type === 'vault'
          ? `{{vault:${r.vault_label ?? r.key.toLowerCase()}}}`
          : (r.defaultValue ?? ''),
        isSecret: r.type === 'secret',
        type: r.type,
        vault_label: r.vault_label,
        readonly: r.type === 'fixed',
      }));
    } else {
      stdio_env_rows = entry.envKeys.map((k) => {
        const isSecret = /token|secret|key|password/i.test(k);
        return { key: k, value: '', isSecret, type: isSecret ? 'secret' as EnvVarType : 'plain' as EnvVarType };
      });
    }

    onChange({
      stdio_catalog: key,
      stdio_command: entry.command ?? 'uvx',
      stdio_package: entry.package ?? '',
      stdio_version: '',
      stdio_env_rows,
    });

    if (entry.registry) {
      setVersionLoading(true);
      try {
        const latest = await fetchLatestStdioVersion(entry);
        onChange({ stdio_version: latest ?? entry.fallbackVersion ?? '' });
      } finally {
        setVersionLoading(false);
      }
    }
  };

  const handleRefreshVersion = async () => {
    if (!catalogEntry.registry) return;
    setVersionLoading(true);
    try {
      const latest = await fetchLatestStdioVersion(catalogEntry);
      onChange({ stdio_version: latest ?? catalogEntry.fallbackVersion ?? '' });
    } finally {
      setVersionLoading(false);
    }
  };

  // ── Env row helpers ───────────────────────────────────────────────────────

  const updateEnvRow = (index: number, partial: Partial<EnvVarRow>) => {
    const rows = [...value.stdio_env_rows];
    rows[index] = { ...rows[index], ...partial };
    onChange({ stdio_env_rows: rows });
  };

  const removeEnvRow = (index: number) => {
    onChange({ stdio_env_rows: value.stdio_env_rows.filter((_, i) => i !== index) });
    setSecretVisible((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const addEnvRow = () => {
    onChange({
      stdio_env_rows: [...value.stdio_env_rows, { key: '', value: '', isSecret: false, type: 'plain' }],
    });
  };

  const toggleSecretVisible = (i: number) => {
    setSecretVisible((prev) => ({ ...prev, [i]: !prev[i] }));
  };

  const toggleRowType = (i: number) => {
    const row = value.stdio_env_rows[i];
    if (row.type === 'fixed' || row.type === 'vault') return; // catalog-locked, can't change
    const next: EnvVarType = row.type === 'secret' ? 'plain' : 'secret';
    updateEnvRow(i, { type: next, isSecret: next === 'secret' });
  };

  // ── Live JSON preview ─────────────────────────────────────────────────────

  const previewConfig = useMemo(() => buildPreviewConfig(value), [value]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* MCP Server catalog */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <FormControl size="small" fullWidth>
          <InputLabel>MCP Server</InputLabel>
          <Select
            label="MCP Server"
            value={value.stdio_catalog}
            onChange={(e: SelectChangeEvent) => applyCatalogEntry(e.target.value as StdioCatalogKey)}
          >
            {(Object.keys(MCP_CATALOG) as StdioCatalogKey[]).map((key) => (
              <MenuItem key={key} value={key}>{MCP_CATALOG[key].label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Choose a pre-built MCP server or select Custom stdio to enter your own. Catalog entries auto-fill the package, command, and required credentials.">
          <IconButton size="small" aria-label="More info" sx={{ p: 0.25, color: 'text.secondary', flexShrink: 0 }}>
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* OCI / catalog warning callout */}
      {catalogEntry.warningNote && (
        <Alert severity="warning" sx={{ py: 0.75 }}>
          <Typography variant="caption" component="span">
            <strong>⚠ Prototype-only server.</strong> {catalogEntry.warningNote}
          </Typography>
        </Alert>
      )}

      {/* Command */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <FormControl size="small" sx={{ maxWidth: 260 }} disabled={isCatalog}>
          <InputLabel>Command</InputLabel>
          <Select
            label="Command"
            value={value.stdio_command}
            onChange={(e: SelectChangeEvent) => onChange({ stdio_command: e.target.value as StdioCommand })}
          >
            {STDIO_COMMANDS.map((cmd) => (
              <MenuItem key={cmd} value={cmd}>{cmd}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="The CLI tool used to run the package. Use uvx for Python packages (PyPI), npx for Node packages (npm), or python for a local script.">
          <IconButton size="small" aria-label="More info" sx={{ p: 0.25, color: 'text.secondary', flexShrink: 0 }}>
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Package + Version */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <TextField
            label="Package"
            size="small"
            fullWidth
            disabled={isCatalog}
            value={value.stdio_package}
            onChange={(e) => onChange({ stdio_package: e.target.value })}
            placeholder="awslabs.aws-api-mcp-server"
            InputProps={{
              endAdornment: (
                <Tooltip title="The exact package name from PyPI (for uvx) or npm (for npx). Find it on pypi.org or npmjs.com in the package's installation instructions.">
                  <IconButton size="small" aria-label="More info" sx={{ p: 0.25, color: 'text.secondary' }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              ),
            }}
          />
          <Typography variant="body2" sx={{ mt: 1.25, color: 'text.secondary' }}>@</Typography>
          <TextField
            label="Version"
            size="small"
            sx={{ minWidth: 140 }}
            value={versionLoading ? '' : value.stdio_version}
            placeholder={versionLoading ? '...' : '1.4.1'}
            disabled={versionLoading}
            onChange={(e) => onChange({ stdio_version: e.target.value })}
            InputProps={{
              endAdornment: (
                <Tooltip title="Pin to a specific release (e.g. 1.3.4). Find available versions on the package's PyPI or npm page.">
                  <IconButton size="small" aria-label="More info" sx={{ p: 0.25, color: 'text.secondary' }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              ),
            }}
          />
          {catalogEntry.registry && (
            <Tooltip title="Fetch latest stable version">
              <span>
                <IconButton size="small" onClick={handleRefreshVersion} disabled={versionLoading}>
                  {versionLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        {value.stdio_version.trim().toLowerCase() === 'latest' && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
            ⚠ Avoid @latest — pin to a specific version
          </Typography>
        )}
      </Box>

      {/* Environment Variables */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" fontWeight={500}>Environment Variables</Typography>
            <Tooltip title="Key: the environment variable name the MCP server expects. Value: the credential or config value. VAULT rows are filled from your secrets store — leave them as-is. FIXED rows are read-only defaults. SECRET values are encrypted and never visible after saving.">
              <IconButton size="small" aria-label="More info" sx={{ p: 0.25, color: 'text.secondary' }}>
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={addEnvRow} sx={{ textTransform: 'none' }}>
            Add variable
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {value.stdio_env_rows.map((row, i) => {
            const effectiveType: EnvVarType = row.type ?? (row.isSecret ? 'secret' : 'plain');
            const isFixed   = effectiveType === 'fixed';
            const isVault   = effectiveType === 'vault';
            const isSecret  = effectiveType === 'secret';
            const isVisible = secretVisible[i] ?? false;
            const badge     = TYPE_BADGE[effectiveType];

            return (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  opacity: isFixed ? 0.75 : 1,
                }}
              >
                {/* Key */}
                <TextField
                  size="small"
                  sx={{ flex: 1, '& input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                  value={row.key}
                  onChange={(e) => updateEnvRow(i, { key: e.target.value })}
                  placeholder="ENV_KEY"
                />

                {/* Value */}
                {isVault ? (
                  <TextField
                    size="small"
                    sx={(theme) => ({
                      flex: 1,
                      '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.75rem', color: theme.palette.text.primary },
                      '& .MuiInputBase-input::placeholder': { color: theme.palette.text.disabled, opacity: 1 },
                    })}
                    value={row.vault_label ?? row.key.toLowerCase()}
                    onChange={(e) => updateEnvRow(i, { vault_label: e.target.value })}
                    placeholder="vault-secret-label"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Enter the vault secret label — resolved from your secrets store at runtime" arrow>
                            <InfoOutlinedIcon sx={{ fontSize: '0.95rem', color: 'text.disabled', cursor: 'default' }} />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : row.key === 'OCI_WRITE_ENABLED' ? (
                  // Boolean-only value — a text input, but only true/false/TRUE/FALSE are
                  // accepted as valid; anything else shows an inline error.
                  <TextField
                    size="small"
                    sx={{ flex: 1, '& input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                    value={row.value}
                    onChange={(e) => updateEnvRow(i, { value: e.target.value })}
                    placeholder="true / false / TRUE / FALSE"
                    error={row.value !== '' && !['true', 'false', 'TRUE', 'FALSE'].includes(row.value)}
                    helperText={
                      row.value !== '' && !['true', 'false', 'TRUE', 'FALSE'].includes(row.value)
                        ? 'Only true, false, TRUE, or FALSE accepted'
                        : undefined
                    }
                  />
                ) : (
                  <TextField
                    size="small"
                    sx={{ flex: 1 }}
                    type={isSecret && !isVisible ? 'password' : 'text'}
                    value={row.value}
                    onChange={(e) => updateEnvRow(i, { value: e.target.value })}
                    placeholder={isSecret ? 'saved — enter new value to change' : 'value'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip
                            title={
                              isFixed
                                ? 'Fixed value — pre-configured by the package'
                                : isSecret
                                  ? 'Encrypted and stored securely — enter a new value to change it'
                                  : 'Plain text value — stored and used as-is'
                            }
                            arrow
                          >
                            <InfoOutlinedIcon sx={{ fontSize: '0.95rem', color: 'text.disabled', cursor: 'default' }} />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                {/* Type badge chip */}
                <Chip
                  label={badge.label}
                  color={badge.color}
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 58, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.04em', cursor: (!isFixed && !isVault) ? 'pointer' : 'default' }}
                  onClick={() => toggleRowType(i)}
                />

                {/* Eye toggle for secret rows */}
                {isSecret ? (
                  <Tooltip title={isVisible ? 'Hide value' : 'Show value'}>
                    <IconButton size="small" onClick={() => toggleSecretVisible(i)}>
                      {isVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                ) : (
                  /* Spacer so delete button stays aligned */
                  <Box sx={{ width: 28 }} />
                )}

                {/* Delete — hidden for fixed rows */}
                {!isFixed ? (
                  <IconButton size="small" color="error" onClick={() => removeEnvRow(i)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <Box sx={{ width: 28 }} />
                )}
              </Box>
            );
          })}

          {value.stdio_env_rows.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No environment variables yet. Click "Add variable" to add one.
            </Typography>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
          Secret values are encrypted and never visible after saving. Vault values are resolved from your secrets store at runtime.
        </Typography>
      </Box>

      {/* Business Rules pre-fill (catalog entries with a template) */}
      {catalogEntry.businessRulesTemplate && onPrefillBusinessRules && (
        <Button
          size="small"
          variant="text"
          startIcon={<ContentPasteIcon fontSize="small" />}
          onClick={() => onPrefillBusinessRules(catalogEntry.businessRulesTemplate!)}
          sx={{ textTransform: 'none', alignSelf: 'flex-start', color: 'text.secondary' }}
        >
          Pre-fill business rules from {value.stdio_catalog.toUpperCase()} runbook
        </Button>
      )}

      {/* auth_config JSON preview (collapsible, for non-custom catalog) */}
      {isCatalog && (
        <Box>
          <Button
            size="small"
            variant="text"
            endIcon={showPreview ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            onClick={() => setShowPreview((p) => !p)}
            sx={{ textTransform: 'none', color: 'text.secondary', px: 0 }}
          >
            auth_config preview
          </Button>
          <Collapse in={showPreview}>
            <Paper
              variant="outlined"
              sx={{
                mt: 0.5,
                p: 1.5,
                backgroundColor: 'background.default',
                overflowX: 'auto',
              }}
            >
              <Box component="span" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>{'{'}</Box>
              {Object.entries(previewConfig).map(([k, v]) => (
                <JsonLine key={k} k={k} v={v} />
              ))}
              <Box component="span" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>{'}'}</Box>

              {/* OCI IAM policy */}
              {catalogEntry.ociIamPolicy && (
                <Box sx={{ mt: 2, borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Required IAM Policy
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      mt: 0.75,
                      fontFamily: 'monospace',
                      fontSize: '0.72rem',
                      lineHeight: 1.8,
                      color: 'text.secondary',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      m: 0,
                    }}
                  >
                    {catalogEntry.ociIamPolicy}
                  </Box>
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.75 }}>
                    ⚠ <strong>manage</strong> is required for Security Lists — <code>use</code> only grants read access.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default StdioMcpFields;